import { getNumbers, share, PROTEIN_KKAL, FAT_KKAL, CARBOHYDRATE_KKAL } from '../../lib/balanceRows';

/**
 * Раздел «Баланс»: три ползунка и углеводы остатком.
 *
 * Коридор задаёт сам контрол: концы ползунка — это min и max цели,
 * выйти за них нельзя по устройству, а не по проверке после ввода
 * (bod-knowledge decisions/balance-controls.md).
 *
 * Пересчёт идёт на месте, без похода на сервер: формулы те же, что
 * на сервере, они лежат в общем модуле. Специалист видит результат
 * до сохранения, а не после пересборки программы.
 */

// Ручка центрируется по границе закрашенной части дорожки, поэтому
// в крайних положениях половина её выступала бы наружу. Дорожку
// укорачиваем на эту половину с каждой стороны — тогда ручка упирается
// ровно в края карточки и горизонтальной прокрутки не появляется.
function handleHalf(handle) {
    return handle ? handle.offsetWidth / 2 : 0;
}

export function initBalance($, apiClient) {
    const root = document.querySelector('.macro-balance');
    if (!root) return;

    const maintenanceCalories = parseFloat(root.getAttribute('data-maintenance'));
    const calculationWeight = parseFloat(root.getAttribute('data-weight'));
    if (!maintenanceCalories || !calculationWeight) return;

    // Дефицит живёт в разделе целей, а не здесь: он про калории, баланс
    // про БЖУ. Но доли БЖУ считаются от калорийности, поэтому следим
    // за полем — иначе цифры разъехались бы с тем, что уйдёт на сервер.
    const modifierField = document.getElementById('calorieModifier');
    const initialModifier = parseFloat(root.getAttribute('data-modifier')) || 0;

    function currentModifier() {
        if (!modifierField) return initialModifier;
        const raw = String(modifierField.value).trim().replace(',', '.');
        if (raw === '') return initialModifier;
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed / 100 : initialModifier;
    }

    const rows = {};
    root.querySelectorAll('.macro-row').forEach(row => {
        rows[row.getAttribute('data-balance')] = row;
    });

    // Текущие значения ползунков. Углеводов здесь нет: они не настройка,
    // а остаток.
    const state = {};
    ['protein', 'fat'].forEach(key => {
        const row = rows[key];
        if (!row || !row.hasAttribute('data-value')) return;
        state[key] = {
            value: parseFloat(row.getAttribute('data-value')),
            min: parseFloat(row.getAttribute('data-min')),
            max: parseFloat(row.getAttribute('data-max')),
            // Умолчание цели. Нужно, чтобы отличить «специалист подвинул»
            // от «специалист открыл карточку и нажал сохранить».
            def: parseFloat(row.getAttribute('data-default')),
            step: parseFloat(row.getAttribute('data-step')) || 0.01,
        };
    });

    function setText(row, selector, text) {
        if (!row) return;
        const node = row.querySelector(selector);
        if (node) node.textContent = text;
    }

    // Положение ручки и закрашенной части по текущему значению.
    //
    // Центр ручки стоит на конце закрашенной части — отсюда выступ
    // за дорожку на половину ширины в крайних положениях. Ход идёт
    // от самого начала дорожки: у классической ручки на линии
    // отдельной точки-минимума нет.
    const FILL_MIN = 0;
    function paint(key) {
        const row = rows[key];
        const item = state[key];
        if (!row || !item) return;
        const track = row.querySelector('.macro-row__track');
        const fill = row.querySelector('.js-balance-fill');
        const handle = row.querySelector('.js-balance-handle');
        if (!track || !handle) return;
        const span = item.max - item.min;
        const ratio = span > 0 ? (item.value - item.min) / span : 0;
        const usable = Math.max(0, track.offsetWidth - FILL_MIN);
        const fillWidth = FILL_MIN + ratio * usable;
        if (fill) fill.style.width = `${fillWidth}px`;
        handle.style.left = `${fillWidth - handleHalf(handle)}px`;
        handle.setAttribute('aria-valuenow', String(Math.round(item.value * 1000) / 1000));
    }

    // Пересчёт всех четырёх чисел. Углеводы считаются от уже округлённых
    // белка и жиров — так же, как на сервере, иначе сумма килокалорий
    // не сойдётся с тем, что на экране.
    function recalculate() {
        const numbers = getNumbers({
            maintenanceCalories,
            calculationWeight,
            calorieModifier: currentModifier(),
            protein: state.protein ? state.protein.value : 0,
            fat: state.fat ? state.fat.value : 0,
        });
        setText(rows.protein, '.js-balance-grams', String(numbers.proteins));
        setText(rows.protein, '.js-balance-share',
            String(share(PROTEIN_KKAL, numbers.proteins, numbers.calories)));
        setText(rows.fat, '.js-balance-grams', String(numbers.fat));
        setText(rows.fat, '.js-balance-share',
            String(share(FAT_KKAL, numbers.fat, numbers.calories)));
        setText(rows.carbohydrate, '.js-balance-grams', String(numbers.carbohydrate));
        setText(rows.carbohydrate, '.js-balance-share',
            String(share(CARBOHYDRATE_KKAL, numbers.carbohydrate, numbers.calories)));
    }

    // Значение по положению пальца. Шаг нужен, чтобы в поле не уезжало
    // число вида 1,6374: специалист выбирает из осмысленной сетки.
    function valueFromPointer(key, clientX) {
        const row = rows[key];
        const item = state[key];
        const track = row.querySelector('.macro-row__track');
        const box = track.getBoundingClientRect();
        const usable = Math.max(1, box.width - FILL_MIN);
        const ratio = (clientX - box.left - FILL_MIN) / usable;
        const raw = item.min + ratio * (item.max - item.min);
        const stepped = Math.round(raw / item.step) * item.step;
        // Концы — это и есть границы коридора: дальше ручка не идёт.
        return Math.min(item.max, Math.max(item.min, stepped));
    }

    function startDrag(key, handle) {
        const move = event => {
            const point = event.touches ? event.touches[0] : event;
            state[key].value = valueFromPointer(key, point.clientX);
            paint(key);
            recalculate();
            // Страница не должна ехать, пока тянут ручку.
            if (event.cancelable) event.preventDefault();
        };
        const stop = () => {
            handle.classList.remove('is-dragging');
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseup', stop);
            document.removeEventListener('touchmove', move);
            document.removeEventListener('touchend', stop);
        };
        // Нажатие на ручку даёт обратный зум — она растёт, а не
        // уменьшается: палец её закрывает, и уменьшение читалось бы
        // как «ускользнула» (ui/title-actions). Класс держится всё
        // перетаскивание, иначе мышью ручка сдувается сразу.
        handle.classList.add('is-dragging');
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', stop);
        document.addEventListener('touchmove', move, { passive: false });
        document.addEventListener('touchend', stop);
    }

    Object.keys(state).forEach(key => {
        const handle = rows[key].querySelector('.js-balance-handle');
        if (!handle) return;
        handle.addEventListener('mousedown', event => {
            event.preventDefault();
            startDrag(key, handle);
        });
        handle.addEventListener('touchstart', event => {
            startDrag(key, handle);
            if (event.cancelable) event.preventDefault();
        }, { passive: false });
        // С клавиатуры — стрелками. Ручка это кнопка, фокус на неё
        // попадает сам, отдельного кода для этого не нужно.
        handle.addEventListener('keydown', event => {
            const item = state[key];
            let delta = 0;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -item.step;
            if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = item.step;
            if (!delta) return;
            event.preventDefault();
            item.value = Math.min(item.max, Math.max(item.min, item.value + delta));
            paint(key);
            recalculate();
        });
    });

    // Первая отрисовка и пересчёт при смене ширины: позиция ручки
    // считается в пикселях от ширины дорожки.
    function paintAll() {
        Object.keys(state).forEach(paint);
    }
    paintAll();
    window.addEventListener('resize', paintAll);
    // Поправили дефицит — числа БЖУ пересчитываются сразу, не дожидаясь
    // сохранения: иначе на экране остались бы граммы от прежних калорий.
    if (modifierField) modifierField.addEventListener('input', recalculate);

    // Значения забирает общая отправка формы параметров клиента.
    // Настройку отправляем, только если ручку действительно двигали.
    //
    // Иначе «открыл и сохранил» записал бы умолчания цели как ручную
    // настройку специалиста, и человек перестал бы получать обновления
    // справочника: явная настройка всегда сильнее умолчания. То есть
    // пустое действие тихо отвязало бы клиента от науки.
    const MOVED = 1e-6;
    root.getBalanceOverrides = () => {
        const overrides = {};
        Object.keys(state).forEach(key => {
            const item = state[key];
            if (!Number.isFinite(item.def) || Math.abs(item.value - item.def) > MOVED) {
                overrides[key] = item.value;
            }
        });
        return overrides;
    };
}
